import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import {
  history,
  Link,
  setLocale,
  useAntdConfigSetter,
  useIntl,
  useModel,
} from '@umijs/max';
import { AiChat } from '@zhiyue/ai-chat';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';

import { theme as antdTheme } from 'antd';
import {
  getBrandAppName,
  getBrandHeaderLogo,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import {
  CORAL_DARK_COMPONENTS,
  CORAL_DARK_TOKENS,
  CORAL_LIGHT_COMPONENTS,
  CORAL_LIGHT_TOKENS,
  ensureDefaultNavTheme,
  getNavigationToken,
  getStoredNavTheme,
} from 'config/themeTokens';
import React from 'react';
import {
  getBootstrapTenantList,
  sessionAdapter,
} from '@/adapters/sessionAdapter';
import {
  AvatarDropdown,
  NotificationIcon,
  SelectLang,
  ThemeToggle,
} from '@/components';
import {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
  HEADER_HEIGHT,
} from '@/components/Layout/layoutConstants';
import SiderResizeHandle from '@/components/Layout/SiderResizeHandle';
import {
  buildWorkspaceMenu,
  MobileWorkspaceMenu,
} from '@/components/WorkspaceNavigation';
import { getMyPlatformPermissions } from '@/modules/platform-management/services/platform-tenants.api';
import { getNavigationDomain, getNavigationItem } from '@/navigation';
import { buildPlatformAccessMap } from '@/permissions/adapter';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  getAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  loadPlatformProfile,
  usePlatformProfile,
} from '@/state/platformProfile';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import defaultSettings from '../config/defaultSettings';
import { readCurrentTenantUuid } from './adapters/tenantStorage';
import { errorConfig } from './requestErrorConfig';
import { saveNavTheme } from './utils/theme';
import './styles/shell.less';
import './styles/list-page.less';
import './styles/flat-surfaces.less';

export const request: RequestConfig = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback'];
const NO_PERMISSION_PATH = '/user/no-permission';

/**
 * 权限查询成功且一个平台权限都没有时，跳到无权限页。
 * 首次登录在 /callback 刷新初始态时会被跳过，所以切换页面时还要再判断一次。
 */
const redirectIfNoPlatformPermission = (state?: {
  platformPermissions?: string[];
  platformPermissionsLoaded?: boolean;
}) => {
  const { pathname } = history.location;
  if (
    state?.platformPermissionsLoaded &&
    (state.platformPermissions ?? []).length === 0 &&
    !publicPaths.includes(pathname) &&
    pathname !== NO_PERMISSION_PATH
  ) {
    history.replace(NO_PERMISSION_PATH);
  }
};

// 需在 @zhiyue/core 首次读取 navTheme 之前执行，故放在模块作用域而非组件内。
ensureDefaultNavTheme();

const getSettingsWithSiderWidth = (locale?: string) => {
  const navTheme = getStoredNavTheme();

  return {
    ...defaultSettings,
    title: getBrandAppName(locale),
    siderWidth: getStoredSiderWidth(),
    // 与 @zhiyue/core 读同一份偏好，避免 ProLayout 的嵌套 ConfigProvider 与外层主题不同步
    navTheme,
    logo: getBrandHeaderLogo(navTheme),
  };
};

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
        // core 未配置时回落到职阅的工作台、企业管理、开放平台地址；空字符串才能关掉这几项
        workbenchUrl: '',
        tenantManagementUrl: '',
        openPlatformUrl: '',
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      // 后台创建应用未指定归属租户时，会归属并开通给当前企业，需要能看到并切换当前企业
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const applicationReturnTo = consumeApplicationAccessReturnTo();
      if (applicationReturnTo) {
        window.location.href = applicationReturnTo;
        return;
      }

      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

// logo 直接订阅 useTheme 而非复用 ProLayout 传入的节点：
// initialState.settings.logo 的更新不会实时传导到该节点，会出现切到深色后
// 仍挂着深色图形的白底 logo。
const HeaderTitle: React.FC<{
  appTitle: string;
}> = ({ appTitle }) => {
  const { navTheme } = useTheme();
  // 订阅平台品牌，平台设置保存后头部 logo 立即刷新
  usePlatformProfile();

  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push('/')}
      >
        <img
          className="app-header-title-logo"
          src={getBrandHeaderLogo(navTheme)}
          alt=""
        />
        <span className="app-header-title-name">{appTitle}</span>
      </button>
    </div>
  );
};

const SessionSync: React.FC = () => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });
  }, [currentUser, loading, shellConfig.featureFlags]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    saveNavTheme(navTheme);

    // global.less 依据该标记决定页面画布底色，避免 antd 的 scoped CSS 变量
    // 无法传导到 html/body 时，浅色主题下仍残留深色画布。
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = useDarkMode ? 'dark' : 'light';
    }

    setAntdConfig((prevConfig) => {
      const prevTheme = prevConfig?.theme || {};
      const normalized = (
        Array.isArray(prevTheme.algorithm)
          ? prevTheme.algorithm
          : prevTheme.algorithm
            ? [prevTheme.algorithm]
            : []
      ).filter((item) => item !== antdTheme.defaultAlgorithm);

      const nextAlgorithms = useDarkMode
        ? [
            ...normalized.filter((item) => item !== antdTheme.darkAlgorithm),
            antdTheme.darkAlgorithm,
          ]
        : normalized.filter((item) => item !== antdTheme.darkAlgorithm);

      const nextAlgorithm =
        nextAlgorithms.length === 1
          ? nextAlgorithms[0]
          : nextAlgorithms.length > 1
            ? nextAlgorithms
            : antdTheme.defaultAlgorithm;

      const nextToken = {
        ...(prevTheme.token || {}),
        ...(useDarkMode ? CORAL_DARK_TOKENS : CORAL_LIGHT_TOKENS),
      };

      const themeComponents = useDarkMode
        ? CORAL_DARK_COMPONENTS
        : CORAL_LIGHT_COMPONENTS;
      const prevComponents = prevTheme.components || {};

      return {
        ...prevConfig,
        wave: { ...prevConfig.wave, disabled: true },
        theme: {
          ...prevTheme,
          algorithm: nextAlgorithm,
          token: nextToken,
          components: {
            ...prevComponents,
            Button: {
              ...prevComponents.Button,
              ...themeComponents.Button,
            },
            Radio: {
              ...prevComponents.Radio,
              ...themeComponents.Radio,
            },
          },
        },
      };
    });
  }, [navTheme, setAntdConfig]);

  return null;
};

/**
 * 把会话、主题、品牌同步进 initialState。
 * rootContainer 位于 initialState 的 Provider 之外，umi 也不会给它传 setInitialState，
 * 因此必须经 innerProvider 挂载，才能通过 useModel 读写。
 */
const InitialStateSync: React.FC = () => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const { navTheme } = useTheme();
  const { currentUser } = useSession();
  const { version: brandingVersion } = usePlatformProfile();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const [appTitle, setAppTitle] = React.useState<string>();

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  // brandingVersion 不在函数体内使用，但平台品牌保存后需要据此重新生成标题与 logo
  React.useEffect(() => {
    setAppTitle(syncBrandDocument(runtimeLocale));
  }, [runtimeLocale, brandingVersion]);

  React.useEffect(() => {
    // umi 的 setInitialState 会顺带把 loading 置为 false，
    // 必须等 getInitialState 返回后再写，且值未变时不写，避免多余的全局重渲染。
    if (!initialState || !appTitle) {
      return;
    }

    const settings = (initialState.settings || {}) as {
      navTheme?: string;
      logo?: string;
      title?: unknown;
    };
    const logo = getBrandHeaderLogo(navTheme);
    if (
      settings.navTheme === navTheme &&
      settings.logo === logo &&
      settings.title === appTitle &&
      getUserSyncKey(initialState.currentUser) === getUserSyncKey(currentUser)
    ) {
      return;
    }

    setInitialState((prev) =>
      prev
        ? {
            ...prev,
            currentUser: (currentUser ?? undefined) as typeof prev.currentUser,
            settings: {
              ...prev.settings,
              navTheme,
              logo,
              title: appTitle,
            },
          }
        : prev,
    );
  }, [
    appTitle,
    brandingVersion,
    currentUser,
    initialState,
    navTheme,
    setInitialState,
  ]);

  return null;
};

export const innerProvider = (container: React.ReactNode) => (
  <>
    <InitialStateSync />
    {container}
  </>
);

export const rootContainer = (container: React.ReactNode) => {
  return <RootSessionContainer>{container}</RootSessionContainer>;
};

const RootSessionContainer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <SessionSync />
      {children}
      <FloatingAiChat />
    </SessionProvider>
  );
};

const FloatingAiChat: React.FC = () => {
  const { currentUser, loading } = useSession();
  const { navTheme } = useTheme();
  const { aiAssistantEnabled } = usePlatformProfile();

  if (
    !aiAssistantEnabled ||
    loading ||
    !currentUser ||
    publicPaths.includes(history.location.pathname)
  ) {
    return null;
  }

  return (
    <AiChat
      title="AI 助手"
      position="bottom-right"
      theme={navTheme === 'realDark' ? 'dark' : 'light'}
      authHeaders={() => {
        const headers: Record<string, string> = {};
        const token = getAccessToken();
        const tenantUuid = readCurrentTenantUuid() || currentUser.tenantUuid;
        const organizationUuid =
          (
            currentUser as CurrentUser & {
              organizationUuid?: string;
              lastLoginOrganizationUuid?: string;
            }
          ).organizationUuid ||
          (
            currentUser as CurrentUser & {
              lastLoginOrganizationUuid?: string;
            }
          ).lastLoginOrganizationUuid;

        if (token) headers.Authorization = `Bearer ${token}`;
        if (tenantUuid) headers['x-tenant-id'] = tenantUuid;
        if (organizationUuid) headers['x-organization-id'] = organizationUuid;
        return headers;
      }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  platformPermissions?: string[];
  /** 权限查询成功；失败时不跳转，交给后端逐个接口拒绝 */
  platformPermissionsLoaded?: boolean;
  loading?: boolean;
}> {
  let platformPermissions: string[] = [];
  let platformPermissionsLoaded = false;
  const platformProfileLoading = loadPlatformProfile();

  // callback 页面里会在 token 落库后主动 refresh 初始态；
  // 这里不能再排除 /callback，否则首登回跳后权限态会一直是空的。
  if (hasOAuth2Session()) {
    const [, permissionResult] = await Promise.allSettled([
      // 预热会话适配器的租户列表缓存
      getBootstrapTenantList(),
      getMyPlatformPermissions({ skipErrorHandler: true }),
    ]);
    if (permissionResult.status === 'fulfilled') {
      platformPermissions = permissionResult.value?.permissions ?? [];
      platformPermissionsLoaded = true;
      redirectIfNoPlatformPermission({
        platformPermissions,
        platformPermissionsLoaded,
      });
    }
  }
  await platformProfileLoading;
  return {
    loading: true,
    platformPermissions,
    platformPermissionsLoaded,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const intl = useIntl();
  const currentLocation = `${history.location.pathname}${history.location.search}`;
  const activeDomain = getNavigationDomain(currentLocation);
  const activeItem = getNavigationItem(currentLocation);
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  // 直接订阅 useTheme 而非读 initialState.settings.navTheme：
  // 后者要等 InitialStateSync 的 effect 回写，切换主题时会慢一帧。
  const { navTheme } = useTheme();
  const { showHeader: shellHeaderVisible } = getCurrentLayoutVisibility();

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    // 历史 settings 或 SettingDrawer 可能改写这些值，展开后再强制写回约定布局
    layout: 'mix',
    splitMenus: true,
    fixedHeader: true,
    fixSiderbar: true,
    contentWidth: 'Fluid',
    navTheme,
    // ProLayout 会用 settings.colorPrimary 在内层 ConfigProvider 覆盖主色，
    // 必须随主题切换，否则深色下仍是浅色主题的近黑主色，选中态与背景融为一体。
    colorPrimary: (navTheme === 'realDark'
      ? CORAL_DARK_TOKENS
      : CORAL_LIGHT_TOKENS
    ).colorPrimary,
    token: getNavigationToken(navTheme),
    menu: {
      locale: false,
      defaultOpenAll: true,
      ignoreFlatMenu: true,
      autoClose: false,
    },
    contentStyle: {
      height: shellHeaderVisible ? `calc(100vh - ${HEADER_HEIGHT}px)` : '100vh',
      overflow: 'auto',
      minWidth: 0,
    },
    menuDataRender: () =>
      buildWorkspaceMenu(
        intl.formatMessage,
        buildPlatformAccessMap(initialState?.platformPermissions),
      ),
    selectedKeys: [
      activeDomain.id,
      `${activeDomain.id}:${activeItem?.id || 'overview'}`,
    ],
    breadcrumbRender: false,
    menuItemRender: (item, dom) =>
      item.path ? <Link to={item.path}>{dom}</Link> : dom,
    // 帮助已收进用户菜单
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <NotificationIcon key="notification" />,
        <ThemeToggle key="themeToggle" />,
        <SelectLang key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return null;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      if (!showNav) return false;
      if (_props.isMobile) {
        return (
          <MobileWorkspaceMenu
            open={!_props.collapsed}
            onClose={() => _props.onCollapse?.(true)}
            siderWidth={siderWidth}
          />
        );
      }
      return dom;
    },
    headerTitleRender: () => <HeaderTitle appTitle={appTitle} />,
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      redirectIfNoPlatformPermission(initialState);
      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          <a className="workspace-skip-link" href="#workspace-main">
            {intl.formatMessage({ id: 'pages.navigation.skipToContent' })}
          </a>
          <div id="workspace-main" tabIndex={-1}>
            {children}
          </div>
          <SiderResizeHandle
            enabled={showNav}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
  };
};
